/* eslint-disable no-restricted-imports -- DialogService is the repository owned wrapper and the only allowed MatDialog consumer */

import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { defer, delay, EMPTY, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { catchError, concatAll, defaultIfEmpty } from 'rxjs/operators';
import { ErrorHandlerService } from '../error-handler';
import { DialogTarget } from './dialog.model';

/**
 * Repository owned wrapper around `MatDialog` and the only allowed `MatDialog` consumer.
 *
 * It offers two ways to show a dialog:
 * - {@link open} opens immediately and can stack on top of an already visible dialog.
 * - {@link queueDialog} serialises requests, so a dialog is only shown once the previously queued
 *   one has closed. Use it whenever unrelated features can request a dialog at the same time and
 *   overlapping dialogs would hide each other.
 *
 * {@link closeAll} closes what is open and discards what is still waiting. Failures while opening a
 * queued dialog, and failures reported by its `afterClosed()` stream, are forwarded to
 * {@link ErrorHandlerService} and never stall the queue.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

  private readonly queue$: Subject<Observable<unknown>> = new Subject();
  private readonly queueSubscription: Subscription = this.queue$
    .pipe(concatAll(), takeUntilDestroyed(this.destroyRef))
    .subscribe();

  /** Bumped by {@link closeAll} so requests queued earlier no longer open when their turn comes. */
  private flushCount: number = 0;

  /** Delay between a queued dialog closing and the next one opening, for a smoother transition. */
  private readonly QUEUED_DIALOG_TRANSITION_DELAY_MS: number = 400;

  /** Opens `target` immediately, bypassing the queue — can stack on top of a visible dialog. */
  public open<TComponent, TData = unknown, TResult = unknown>(
    target: DialogTarget<TComponent>,
    config?: MatDialogConfig<TData>,
  ): MatDialogRef<TComponent, TResult> {
    return this.dialog.open<TComponent, TData, TResult>(target, config);
  }

  /**
   * Opens `target` once every dialog queued before it has closed. Returns an observable that emits
   * the `MatDialogRef` once opened and then completes, or emits `undefined` when the dialog is never
   * shown — either because {@link closeAll} discarded the request before its turn or because opening
   * failed. Opening failures are reported to {@link ErrorHandlerService}, so callers that cannot act
   * on them do not need their own error handling.
   */
  public queueDialog<TComponent, TData = unknown, TResult = unknown>(
    target: DialogTarget<TComponent>,
    config?: MatDialogConfig<TData>,
  ): Observable<MatDialogRef<TComponent, TResult> | undefined> {
    const dialogRef$: ReplaySubject<MatDialogRef<TComponent, TResult> | undefined> = new ReplaySubject(1);

    this.queue$.next(
      this.createDialogQueueItem(target, config, dialogRef$),
    );

    // The queue stops draining once the service is destroyed, so complete waiting requests instead
    // of leaving their subscribers hanging.
    return dialogRef$.pipe(
      takeUntilDestroyed(this.destroyRef),
      defaultIfEmpty(undefined),
    );
  }

  /**
   * Closes every open dialog and discards requests still waiting in the queue: their observables
   * emit `undefined` and complete once the queue reaches them, and every discard is logged as a
   * warning.
   */
  public closeAll(): void {
    this.flushCount++;
    this.dialog.closeAll();
  }

  /**
   * Builds the queue entry for one request: it opens `target` when its turn comes, keeps the queue
   * on the entry until the opened dialog closes, and settles `dialogRef$` with the reference, or
   * with `undefined` when {@link closeAll} discarded the request meanwhile or opening threw.
   */
  private createDialogQueueItem<TComponent, TData, TResult>(
    target: DialogTarget<TComponent>,
    config: MatDialogConfig<TData> | undefined,
    dialogRef$: Subject<MatDialogRef<TComponent, TResult> | undefined>,
  ): Observable<unknown> {
    const dialogFlushCount: number = this.flushCount;

    return defer(() => {
      if (dialogFlushCount !== this.flushCount) {
        console.warn('DialogService: a queued dialog was discarded by closeAll() before it could be shown.');

        dialogRef$.next(undefined);
        dialogRef$.complete();

        return EMPTY;
      }

      const ref: MatDialogRef<TComponent, TResult> = this.open(target, config);

      dialogRef$.next(ref);
      dialogRef$.complete();

      return ref.afterClosed();
    }).pipe(
      delay(this.QUEUED_DIALOG_TRANSITION_DELAY_MS),
      catchError(error => {
        this.errorHandler.handleError(error);

        // A failure the caller cannot act on: settle as "not shown" instead of erroring, so every
        // consumer only has to handle the missing reference. Both calls are a no-op once the
        // reference was already emitted and `afterClosed()` failed afterwards.
        dialogRef$.next(undefined);
        dialogRef$.complete();

        return EMPTY;
      }),
    );
  }
}
